import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import {
  useChecklistTemplates,
  useCreateChecklistTemplate,
} from '../features/templates/checklistApi'
import { LIFECYCLE_PHASES, type LifecyclePhase } from '../features/templates/phases'
import { useCreateTimelineTemplate, useTimelineTemplates } from '../features/templates/timelineApi'

export function TemplatesPage() {
  const { data: templates, isPending, isError } = useTimelineTemplates()
  const create = useCreateTimelineTemplate()
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const { data: checklists, isPending: checklistsPending } = useChecklistTemplates()
  const createChecklist = useCreateChecklistTemplate()
  const [checklistName, setChecklistName] = useState('')
  const [checklistPhase, setChecklistPhase] = useState<LifecyclePhase>('OnsiteSetup')
  const [checklistError, setChecklistError] = useState<string | null>(null)

  const onCreateChecklist = async (event: React.FormEvent) => {
    event.preventDefault()
    setChecklistError(null)
    try {
      await createChecklist.mutateAsync({
        name: checklistName.trim(),
        description: null,
        defaultPhase: checklistPhase,
      })
      setChecklistName('')
    } catch (err) {
      setChecklistError(err instanceof ApiError ? err.detail : 'Could not create the checklist.')
    }
  }

  const onCreate = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    try {
      await create.mutateAsync({ name: name.trim(), description: null })
      setName('')
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'Could not create the template.')
    }
  }

  return (
    <section>
      <h1>Templates</h1>

      <div className="panel">
        <h2>Timeline templates</h2>
        <p className="muted">
          Date-driven milestones, written once as offsets from the event date so one template serves
          every event.
        </p>

        <form className="inline-form" onSubmit={onCreate}>
          <div className="field">
            <label htmlFor="new-template">New template name</label>
            <input
              id="new-template"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Standard Event Timeline"
            />
          </div>
          <button type="submit" className="primary" disabled={!name.trim() || create.isPending}>
            Create
          </button>
        </form>

        {error ? (
          <p role="alert" className="error">
            {error}
          </p>
        ) : null}

        {isPending ? <p>Loading templates…</p> : null}
        {isError ? (
          <p role="alert" className="error">
            Could not load templates.
          </p>
        ) : null}

        {templates && templates.length === 0 ? (
          <p className="empty">No timeline templates yet.</p>
        ) : null}

        {templates && templates.length > 0 ? (
          <ul className="card-list">
            {templates.map((template) => (
              <li key={template.id} className={template.isRetired ? 'card row-inactive' : 'card'}>
                <Link className="card-title" to={`/templates/timeline/${template.id}`}>
                  {template.name}
                </Link>
                <div className="card-meta">
                  <span>Version {template.version}</span>
                  <span>
                    {template.milestoneCount} milestone
                    {template.milestoneCount === 1 ? '' : 's'}
                  </span>
                  {template.latestPublishedVersion ? (
                    <span>Published v{template.latestPublishedVersion}</span>
                  ) : (
                    <span>Never published</span>
                  )}
                </div>
                <div className="card-tags">
                  <span className={`status status-${template.status.toLowerCase()}`}>
                    {template.status}
                  </span>
                  {template.isRetired ? <span className="badge">Retired</span> : null}
                  {template.isUsedByAPlan ? <span className="badge">In use</span> : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="panel">
        <h2>Checklist templates</h2>
        <p className="muted">
          Ordered task lists executed at a point in the process. Order is the process, so items keep
          the order they were authored in.
        </p>

        <form className="inline-form" onSubmit={onCreateChecklist}>
          <div className="field">
            <label htmlFor="new-checklist">New checklist name</label>
            <input
              id="new-checklist"
              value={checklistName}
              onChange={(e) => setChecklistName(e.target.value)}
              placeholder="Onsite Setup Checklist"
            />
          </div>

          <div className="field">
            <label htmlFor="new-checklist-phase">Default phase</label>
            <select
              id="new-checklist-phase"
              value={checklistPhase}
              onChange={(e) => setChecklistPhase(e.target.value as LifecyclePhase)}
            >
              {LIFECYCLE_PHASES.map((phase) => (
                <option key={phase.value} value={phase.value}>
                  {phase.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="primary"
            disabled={!checklistName.trim() || createChecklist.isPending}
          >
            Create
          </button>
        </form>

        {checklistError ? (
          <p role="alert" className="error">
            {checklistError}
          </p>
        ) : null}

        {checklistsPending ? <p>Loading checklists…</p> : null}

        {checklists && checklists.length === 0 ? (
          <p className="empty">No checklist templates yet.</p>
        ) : null}

        {checklists && checklists.length > 0 ? (
          <ul className="card-list">
            {checklists.map((checklist) => (
              <li key={checklist.id} className={checklist.isRetired ? 'card row-inactive' : 'card'}>
                <Link className="card-title" to={`/templates/checklist/${checklist.id}`}>
                  {checklist.name}
                </Link>
                <div className="card-meta">
                  <span>Version {checklist.version}</span>
                  <span>{checklist.defaultPhaseDisplayName}</span>
                  <span>
                    {checklist.sectionCount} section{checklist.sectionCount === 1 ? '' : 's'},{' '}
                    {checklist.itemCount} item{checklist.itemCount === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="card-tags">
                  <span className={`status status-${checklist.status.toLowerCase()}`}>
                    {checklist.status}
                  </span>
                  {checklist.isRetired ? <span className="badge">Retired</span> : null}
                  {checklist.isUsedByAPlan ? <span className="badge">In use</span> : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  )
}
